import React, { CSSProperties, useEffect, useState } from 'react';
import {
  Tooltip,
  Button,
  Select,
  FluentProvider,
  webLightTheme,
  Field,
} from '@fluentui/react-components';
import { InfoRegular } from '@fluentui/react-icons';
import { ISelectFieldsetProps } from '../../../../utils/types/IFieldsetProps';
import IDropdownReference from '../../../../utils/types/IDropdownReference';
import IDropdownOption from '../../../../utils/types/IDropdownOption';
import { offsetDateIconStyles } from '../../../companies/processes/panels/milestoneDueDateUtils';

const QueryBasedDropdown: React.FunctionComponent<ISelectFieldsetProps> = (
  props: ISelectFieldsetProps,
): JSX.Element => {
  const {
    id,
    label,
    ariaLabel,
    required,
    value,
    handleChange,
    valueKey,
    dropdownOptionsData,
    errors,
    errorMessage,
    width,
    disabled,
    tooltipProps,
    clearable,
    wrapOptionText,
  } = props;
  const [defaultKey, setDefaultKey] = useState<number>(value?.id);
  const noOptions = dropdownOptionsData && dropdownOptionsData.length === 0;
  let dropdownOptions: IDropdownOption[] = [];
  if (dropdownOptionsData) {
    dropdownOptions = dropdownOptionsData.map(
      (dropdownOption: IDropdownReference): IDropdownOption => {
        const isSelectable = dropdownOption.hasOwnProperty('isSelectable')
          ? dropdownOption.isSelectable
          : true;
        return {
          key: dropdownOption.id,
          text: dropdownOption.fieldName,
          ariaLabel: dropdownOption.fieldName === '--' ? 'None' : dropdownOption.fieldName,
          disabled: !isSelectable,
        };
      },
    );
    if (clearable && !required) {
      dropdownOptions.unshift({ key: null, text: '--' });
    }
  }

  const getErrors = (): string => {
    if (errors && errors[valueKey]) {
      return errors[valueKey].message;
    }
    return '';
  };

  const styles: CSSProperties = {
    width: width || 300,
    ...(wrapOptionText && { overflow: 'visible', whiteSpace: 'normal' }),
  };

  const dropdownWrapperStyle: CSSProperties = {
    display: 'flex',
  };

  useEffect(() => {
    setDefaultKey(value?.id || null);
  }, [value]);

  return (
    <FluentProvider theme={webLightTheme} style={dropdownWrapperStyle}>
      <Field label={label} required={required} validationMessage={errorMessage || getErrors()}>
        <Select
          id={id}
          aria-label={ariaLabel}
          value={defaultKey !== null && defaultKey !== undefined ? defaultKey.toString() : ''}
          onChange={(event: React.ChangeEvent<HTMLSelectElement>): void => {
            const selectedKey = event.target.value;
            if (!selectedKey) {
              setDefaultKey(null);
              const entityToSave: IDropdownReference = {
                id: null,
                fieldName: event.target.options[event.target.selectedIndex].text,
              };
              handleChange(entityToSave, valueKey.toString());
            } else {
              const selectedId = parseInt(selectedKey, 10);
              setDefaultKey(selectedId);
              const selectedOption = dropdownOptions.find((option) => option.key === selectedId);
              const entityToSave: IDropdownReference = {
                id: selectedId,
                fieldName: selectedOption ? selectedOption.text : '',
              };
              handleChange(entityToSave, valueKey.toString());
            }
          }}
          disabled={disabled || noOptions}
          placeholder={noOptions ? 'There are no options' : 'Select an option'}
          style={styles}
        >
          <option value="" disabled>
            Select an option
          </option>
          {dropdownOptions.map((option) => (
            <option key={option.key} value={option.key} disabled={option.disabled}>
              {option.text}
            </option>
          ))}
        </Select>
      </Field>
      {tooltipProps?.showTooltip && (
        <Tooltip withArrow content={tooltipProps.tooltipContent} relationship="label">
          <Button
            appearance="subtle"
            aria-label={tooltipProps.tooltipContent}
            icon={<InfoRegular />}
            style={offsetDateIconStyles}
          />
        </Tooltip>
      )}
    </FluentProvider>
  );
};
export default QueryBasedDropdown;
